import { Globe } from 'lucide-react';
import { useEffect, useRef } from 'react';

/**
 * Google Translate widget — script is injected lazily by this React
 * component, AFTER the target #google_translate_element exists in the DOM.
 *
 * Lifecycle:
 *  1. On mount, expose window.googleTranslateElementInit (the callback that
 *     Google's loader script calls once the library is ready).
 *  2. Inject the loader script tag. The callback fires when ready and
 *     instantiates the widget into our React-controlled element.
 *  3. Defensive: if the script has already been injected (e.g. route
 *     re-mount), reuse it.
 */

const TRANSLATE_SCRIPT_ID = 'google-translate-script';
const TRANSLATE_SCRIPT_SRC = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
const TARGET_ID = 'google_translate_element';

let widgetRendered = false;

const renderWidget = () => {
  if (typeof window === 'undefined' || !window.google || !window.google.translate || !window.google.translate.TranslateElement) {
    return false;
  }
  const el = document.getElementById(TARGET_ID);
  if (!el) return false;

  el.innerHTML = '';
  try {
    // eslint-disable-next-line no-new
    new window.google.translate.TranslateElement(
      {
        pageLanguage: 'en',
        // No includedLanguages → Google's full supported language catalog
        // (~130+ languages) is shown in the dropdown.
        // VERTICAL layout renders a native <select> element, giving us a
        // standard scrollable A→Z dropdown (instead of the multi-column
        // table that SIMPLE/HORIZONTAL inject inside an unstylable iframe).
        layout: window.google.translate.TranslateElement.InlineLayout.VERTICAL,
        autoDisplay: false,
      },
      TARGET_ID,
    );
    widgetRendered = true;
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[GoogleTranslate] mount failed:', err);
    return false;
  }
};

/**
 * Variants:
 *  - "compact" (default) — the slim 36px-tall gold-bordered chip used in
 *    the member + admin mobile top bars. On phones it further collapses to
 *    a 44px globe-only tap target (see App.css mobile rules).
 *  - "full" — the uncompressed widget used in the public marketing header.
 *    The native `<select>` is fully readable ("Select Language ▼") at every
 *    viewport. Phones do NOT collapse this variant to the globe-only chip.
 */
const GoogleTranslate = ({ variant = 'compact' }) => {
  const pollRef = useRef(null);

  useEffect(() => {
    // Bind the callback Google's loader will invoke once it's ready.
    window.googleTranslateElementInit = renderWidget;

    const existing = document.getElementById(TRANSLATE_SCRIPT_ID);

    if (existing && window.google && window.google.translate && window.google.translate.TranslateElement) {
      // Library already loaded once — render immediately.
      renderWidget();
    } else {
      // Inject (or re-inject) the loader script. Removing and re-adding
      // guarantees Google invokes our callback after this React effect runs.
      if (existing) existing.remove();
      const s = document.createElement('script');
      s.id = TRANSLATE_SCRIPT_ID;
      s.src = TRANSLATE_SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onerror = () => {
        // eslint-disable-next-line no-console
        console.warn('[GoogleTranslate] failed to load library');
      };
      document.head.appendChild(s);
    }

    // Polling fallback in case the callback path stalls.
    let tries = 0;
    pollRef.current = setInterval(() => {
      tries += 1;
      const el = document.getElementById(TARGET_ID);
      const hasContent = el && el.innerHTML.length > 0;
      if (hasContent || tries > 60) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      } else if (!hasContent) {
        renderWidget();
      }
    }, 350);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const wrapperClass = variant === 'full' ? 'sc-translate sc-translate--full' : 'sc-translate';

  return (
    <div className={wrapperClass} data-testid="google-translate-widget" data-variant={variant}>
      <Globe className={variant === 'full' ? 'w-4 h-4 text-[#FFB612]' : 'w-3.5 h-3.5 text-[#FFB612]'} aria-hidden="true" />
      {/* biome-ignore lint/a11y/useSemanticElements: Google Translate injects its own <select> into this mount point; a <fieldset> would add default UA styling */}
      <div id={TARGET_ID} role="group" aria-label="Language selector" />
    </div>
  );
};

// eslint-disable-next-line no-unused-vars
const _exportSuppressLint = widgetRendered;

export default GoogleTranslate;
